/**
 * How the date or period of a list is written (spec-63 E5, design §4.7): weekday and the one date
 * notation of the platform ("do 12 nov 2026"), a period as "do 12 nov 2026 t/m za 14 nov 2026", and
 * the relative position to today for a tooltip ("over 3 dagen", "vandaag", "2 dagen geleden").
 * Days are plain `YYYY-MM-DD` Amsterdam calendar days.
 */
import { formatDatum } from '@/lib/format'

const WEEKDAY = new Intl.DateTimeFormat('nl-NL', { weekday: 'short', timeZone: 'UTC' })
const MONTH = new Intl.DateTimeFormat('nl-NL', { month: 'short', timeZone: 'UTC' })

const noon = (day: string): Date => new Date(`${day}T12:00:00Z`)

/** "do", "vr" … */
export function weekdayOf(day: string): string {
  return WEEKDAY.format(noon(day)).replace('.', '')
}

/** "nov" … */
export function monthOf(day: string): string {
  return MONTH.format(noon(day)).replace('.', '')
}

/** "12" … */
export function dayOfMonth(day: string): string {
  return String(Number(day.slice(8, 10)))
}

/** "do 12 nov 2026". */
export function weekdayDate(day: string): string {
  return `${weekdayOf(day)} ${formatDatum(day)}`
}

/** The date of a list, or its period with "t/m"; null for a list without a date. */
export function listWhen(startsOn: string | null, endsOn: string | null): string | null {
  if (!startsOn) return null
  if (!endsOn || endsOn === startsOn) return weekdayDate(startsOn)
  return `${weekdayDate(startsOn)} t/m ${weekdayDate(endsOn)}`
}

function dayDiff(from: string, to: string): number {
  return Math.round((noon(to).getTime() - noon(from).getTime()) / 86_400_000)
}

/** Where the list is in time, for a `title`: "vandaag", "morgen", "over 3 dagen", "gisteren", "2 dagen geleden", "loopt nog (t/m …)". */
export function listRelative(startsOn: string | null, endsOn: string | null, today: string): string | null {
  if (!startsOn) return null
  const last = endsOn ?? startsOn
  if (startsOn <= today && today <= last) return startsOn === last ? 'vandaag' : `loopt nog, t/m ${weekdayDate(last)}`
  if (startsOn > today) {
    const days = dayDiff(today, startsOn)
    return days === 1 ? 'morgen' : `over ${days} dagen`
  }
  const days = dayDiff(last, today)
  return days === 1 ? 'gisteren' : `${days} dagen geleden`
}
